const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const safeText = (value) => String(value ?? "").replace(/_/g, " ").trim();

const ensureMdExtension = (path) =>
  String(path || "").toLowerCase().endsWith(".md") ? path : `${path}.md`;

const isProductionDashboardHost = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "dash.zinduaschool.com";
};

const DEFAULT_NON_PROD_LESSON_BUCKET_NAME = "test-folder";
const DEFAULT_NON_PROD_LESSON_PUBLIC_URL_BASE =
  "https://internal-test.zinduaschool.com";

const safeUrlHost = (value) => {
  try {
    return new URL(String(value || "")).host;
  } catch {
    return "";
  }
};

const getNonProdLessonBucketName = () =>
  String(
    import.meta.env.VITE_LESSON_R2_BUCKET_NAME ||
      DEFAULT_NON_PROD_LESSON_BUCKET_NAME,
  ).trim();

const getNonProdLessonPublicUrlBase = () =>
  String(
    import.meta.env.VITE_LESSON_R2_PUBLIC_URL ||
      DEFAULT_NON_PROD_LESSON_PUBLIC_URL_BASE,
  ).trim();

const normalizeUrlBase = (value) => String(value || "").trim().replace(/\/+$/g, "");

export const getLessonMarkdownUploadTarget = () => {
  if (isProductionDashboardHost()) {
    return {
      bucketName: String(import.meta.env.VITE_BUCKET_NAME || "").trim(),
      publicUrlBase: normalizeUrlBase(import.meta.env.VITE_R2_PUBLIC_URL),
    };
  }

  return {
    bucketName: getNonProdLessonBucketName(),
    publicUrlBase: normalizeUrlBase(getNonProdLessonPublicUrlBase()),
  };
};

/**
 * For edits, we want to overwrite the object backing the *existing* public URL.
 * When editing from local/staging, choosing the upload bucket solely based on
 * window.location.hostname can upload to the wrong bucket.
 */
export const getLessonMarkdownUploadTargetForPublicUrl = (publicUrl) => {
  try {
    const u = new URL(String(publicUrl || "").trim());

    const prodBucketName = String(import.meta.env.VITE_BUCKET_NAME || "").trim();
    const prodPublicUrlBase = normalizeUrlBase(import.meta.env.VITE_R2_PUBLIC_URL);
    const prodHost = safeUrlHost(prodPublicUrlBase);

    const nonProdBucketName = getNonProdLessonBucketName();
    const nonProdPublicUrlBase = normalizeUrlBase(getNonProdLessonPublicUrlBase());
    const nonProdHost = safeUrlHost(nonProdPublicUrlBase);

    if (prodHost && u.host === prodHost) {
      return { bucketName: prodBucketName, publicUrlBase: prodPublicUrlBase };
    }

    if (nonProdHost && u.host === nonProdHost) {
      return { bucketName: nonProdBucketName, publicUrlBase: nonProdPublicUrlBase };
    }

    // Known legacy/alternate hosts that still map to the prod lesson bucket.
    if (u.host === "lessons.zinduaschool.com" || u.host === "lms-internal.zinduaschool.com") {
      return {
        bucketName: prodBucketName,
        publicUrlBase: normalizeUrlBase(u.origin),
      };
    }

    return null;
  } catch {
    return null;
  }
};

export const getLessonMarkdownAllowedHosts = () => {
  const hosts = new Set();

  const envPublicHost = safeUrlHost(import.meta.env.VITE_R2_PUBLIC_URL);
  if (envPublicHost) hosts.add(envPublicHost);

  const nonProdHost = safeUrlHost(getNonProdLessonPublicUrlBase());
  if (nonProdHost) hosts.add(nonProdHost);

  hosts.add("lessons.zinduaschool.com");
  hosts.add("lms-internal.zinduaschool.com");

  return Array.from(hosts);
};

const isProbablyUrl = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    new URL(trimmed);
    return true;
  } catch {
    return false;
  }
};

/**
 * Adds a cache-busting query param to lesson markdown URLs for our known R2 hosts.
 * This prevents stale reads when an object key is overwritten in-place.
 *
 * Note: We do NOT persist this URL; it's intended only for fetching.
 */
export const withLessonMarkdownCacheBust = (value, seed) => {
  if (!isProbablyUrl(value)) return value;

  try {
    const u = new URL(String(value).trim());
    const allowedHosts = new Set(getLessonMarkdownAllowedHosts());
    if (allowedHosts.size > 0 && !allowedHosts.has(u.host)) {
      return value;
    }
    u.searchParams.set("t", String(seed ?? Date.now()));
    return u.toString();
  } catch {
    return value;
  }
};

/**
 * Builds the R2 object key for lesson markdown, matching the required structure.
 *
 * Staging/local (anything except dash.zinduaschool.com):
 *   lessons/{courseSlug}/{topicOrder}-{topicTitle}/{lessonOrder}-{lessonTitle}.md
 *
 * Production (dash.zinduaschool.com):
 *   {courseSlug}/{topicOrder}-{topicTitle}/{lessonOrder}-{lessonTitle}.md
 */
export const buildLessonMarkdownObjectKey = ({
  courseSlug,
  topicOrder,
  topicTitle,
  topicId,
  lessonOrder,
  lessonTitle,
}) => {
  const safeCourse = slugify(courseSlug) || "unknown-course";

  const topicOrderText = String(topicOrder ?? "").trim();
  const topicTitleText = safeText(topicTitle);
  const topicTitleSlug = slugify(topicTitleText);

  const topicSegmentBase =
    topicOrderText && topicTitleSlug
      ? `${topicOrderText}-${topicTitleSlug}`
      : topicTitleSlug
      ? topicTitleSlug
      : topicOrderText
      ? topicOrderText
      : `topic-${String(topicId ?? "unknown")}`;

  const lessonOrderText = String(lessonOrder ?? "").trim() || "1";
  const lessonTitleSlug = slugify(safeText(lessonTitle)) || `lesson-${Date.now()}`;
  const lessonSegment = `${lessonOrderText}-${lessonTitleSlug}`;

  const prefix = isProductionDashboardHost() ? "" : "lessons";
  const path = prefix
    ? `${prefix}/${safeCourse}/${topicSegmentBase}/${lessonSegment}`
    : `${safeCourse}/${topicSegmentBase}/${lessonSegment}`;

  return ensureMdExtension(path);
};
